import { V2App, ComponentType } from '@wix/ambassador-app-service-webapp/types';
import type { RegularAppData } from './appMarket.types';
import { getQueryParams } from '../../../../utils';
import { tpaUtils } from '../../../shared/util';
import { App, AppManagerApiDependencies } from '../../../../types/appManager';
import { AppData } from '@wix/document-services-types';
import { UnifiedWidget } from '@wix/document-services-types/src/platformObject';

interface AppDataWithVersions extends AppData {
  latestVersion?: string;
  blocksVersion?: string;
}

const TEST_VERSION = 'latest';

export const compareAppsByAppManagerPredefinedList =
  (appsList: string[]) =>
  <T extends V2App>(app1: T, app2: T) => {
    const app1Index = appsList.indexOf(app1.id ?? '');
    const app2Index = appsList.indexOf(app2.id ?? '');

    if (app1Index === -1) {
      return 1;
    }
    if (app2Index === -1) {
      return -1;
    }

    return app1Index - app2Index;
  };

export const getAppData =
  (appsList: string[]) =>
  (app?: V2App): Partial<RegularAppData> | undefined => {
    return (
      app && {
        id: app.id,
        name: app.name,
        description: app.market?.marketListing?.basicInfo?.teaser || '',
        icon: app.market?.marketListing?.assetsMap?.appIcon?.assets?.[0]?.url,
        slug: app.market?.slug || '',
        version: app.version,
        isPublished:
          (app.id && appsList.includes(app.id)) || app.status === 'PUBLISHED',
      }
    );
  };

const addCaretPrefixIfNeeded = (str: string) =>
  str.startsWith('^') ? str : `^${str}`;

export const getAppVersionToInstall = (app: RegularAppData) => {
  // There is a case when the editor is opened with the QS of what app version should be installed.
  // In order to support this capability, we need to parse app market params and install a specific version of an app if it was provided
  const queryParams = getQueryParams();
  const appMarketQS = queryParams.appMarketParams;

  if (appMarketQS) {
    const parsedParams = tpaUtils.parseAppMarketQueryParam(appMarketQS);

    if (parsedParams?.appDefId === app.id && parsedParams?.version) {
      return parsedParams.version;
    }
  }

  return addCaretPrefixIfNeeded(app.version);
};

export const isUpdateAvailable = (
  appMarketData: App,
  deps: AppManagerApiDependencies,
) => {
  const appData = deps.getAppDataByAppDefId(appMarketData.id) as AppData;

  if (!appData) {
    throw new Error(
      `No application data found for appDefinitionId: ${appMarketData.id}`,
    );
  }
  if (!appData.appFields?.semanticVersion && !appData.version) {
    return false;
  }

  const app: AppDataWithVersions = {
    ...appData,
    latestVersion: appMarketData.version,
    blocksVersion: getBlocksVersion(appData as AppData),
  };
  const hasUpdate =
    app.latestVersion &&
    getVersion(app.latestVersion).major !==
      getVersion(app.appFields?.semanticVersion || app.version).major;
  const installedVersion = (appData as AppData)?.appFields?.installedVersion;
  const isResponsiveBlocksVersion = Boolean(
    app.blocksVersion?.startsWith('2.'),
  );

  if (hasUpdate && installedVersion !== TEST_VERSION) {
    if (isResponsiveBlocksVersion) {
      return isBlocksUpdateAvailable(app);
    }
    return true;
  }
  return false;
};

const getVersion = (version?: string) => {
  if (!version) {
    return {
      major: 0,
      minor: 0,
    };
  }

  const cleanVersion = version.replace(/^\^/, '');

  const splitVersion = cleanVersion.split('.').map((v) => parseInt(v, 10));
  return {
    major: splitVersion[0],
    minor: splitVersion[1],
  };
};

const getBlocksVersion = (appData: AppData) => {
  const studioWidgetComponent = appData?.components?.find(
    ({ type }) => type === ComponentType.STUDIO_WIDGET,
  );

  if (!studioWidgetComponent) {
    // only Responsive Blocks (2.0.0) supports apps with CODE_PACKAGE or BACK_OFFICE_PAGE component
    const isResponsiveBlocks = appData?.components?.some(
      ({ type }) =>
        type &&
        [ComponentType.CODE_PACKAGE, ComponentType.BACK_OFFICE_PAGE].includes(
          type as ComponentType,
        ),
    );

    if (isResponsiveBlocks) {
      return '2.0.0';
    }
  }

  return (studioWidgetComponent?.data as UnifiedWidget)?.blocksVersion;
};

const isBlocksUpdateAvailable = (app: AppDataWithVersions) => {
  const isMinorUpdatesOn =
    app?.appFields?.installedVersion?.substring(0, 1) === '^';
  const hasMajorUpdate =
    getVersion(app.version)?.major !== getVersion(app.latestVersion)?.major;

  return hasMajorUpdate || !isMinorUpdatesOn;
};
